import { useQuery, useMutation } from "@apollo/react-hooks";
import { useParams } from "react-router-dom";
import gql from "graphql-tag";
import Permissions from "../configs/permissions";
import usePermissions from "../hooks/permissions";
import { SURVEY_MODEL_NAME } from "./../configs/constants";
import { surveyDetails } from "./../graphql/queries/vabooSurveyQueries";
import { updateSurveyMutation } from "./../graphql/mutations/vabooSurveyMutations";
import useBaseFieldSetters from "./../hooks/baseFieldSetters";
import useBaseNavigation from "./../hooks/baseNavigation";
import useDataProvider from "./../hooks/dataProvider";
import useFormErrors from "./../hooks/formErrors";
import useHyperState from "./../hooks/hyperState";
import {
  AddButton,
  CancelButton,
  DeleteButton,
  FormControls,
  SaveButton,
} from "./../mood-ui/Controls";
import {
  RichTextEditorField,
  SelectField,
  TextField,
  ReadOnlyField,
  MultiSelectField,
} from "./../mood-ui/Fields";
import {
  BlockContainer,
  Col,
  FormContainer,
  FormData,
  PageContainer,
  Row,
} from "./../mood-ui/Layout";
import { Heading, ItemsNotFound } from "./../mood-ui/Typography";
import React, { useState } from "react";
import {
  TableContainer,
  TableView,
  TCol,
  TColBorderless,
  TContent,
  THCol,
  THeading,
  TRow,
} from "../mood-ui/DataTable";
import PageSpinner from "../mood-ui/PageSpinner";

const SURVEY_DETAILS = gql(surveyDetails);
const UPDATE_SURVEY = gql(updateSurveyMutation);

const SurveyCreate = () => {
  const { protectPage } = usePermissions();
  protectPage(Permissions.Mutation.CreateSurvey);
  const { fieldError, resolveMutationErrors, clearError } = useFormErrors();

  const { goBack } = useBaseNavigation(SURVEY_MODEL_NAME);

  const { id } = useParams();

  const [landlords] = useDataProvider("landlords");

  const [labels] = useDataProvider("labels");

  const [surveyTypes] = useDataProvider("surveyTypes");

  const [questions] = useDataProvider("questions", {
    filters: { status: ["Published"], usageType: ["Survey"] },
  });

  const {
    state: formData,
    setState: setFormData,
    stateMethods,
  } = useHyperState("formData", {
    title: "",
    reusable: false,
    description: "",
    survey_type_id: 0,
  });

  const [questionIds, setQuestionIds] = useState([]);

  const [usage, setUsage] = useState([]);

  const { setFieldValue: handleFieldOnChange } = useBaseFieldSetters(
    formData,
    stateMethods,
    clearError,
  );

  useQuery(SURVEY_DETAILS, {
    variables: {
      id,
    },
    fetchPolicy: "cache-and-network",
    onCompleted: (result) => {
      if (!result || !result.survey) return false;
      setFormData({ ...result.survey, question_ids: questionIds });
      setQuestionIds(result.survey.question_ids);
      setUsage(
        result.survey.usage.map(({ client_id, label_ids }) => ({
          client_id,
          label_ids,
        })),
      );
    },
    onError: (e) => {
      console.log("e", e);
    },
  });

  const [
    updateSurvey,
    { data: updateResult, loading: isUpdating, error: updateError },
  ] = useMutation(UPDATE_SURVEY, {
    variables: { ...formData },
    onCompleted: (result) => {
      goBack();
    },
    onError: (e) => {
      resolveMutationErrors(e);
    },
  });

  const handleSave = () => {
    const data = { ...formData };
    data.question_ids = questionIds.filter(Boolean).join(",");
    data.usage = usage.map((item) => ({
      ...item,
      client_id: parseInt(item.client_id, 10),
      label_ids: Array.isArray(item.label_ids)
        ? item.label_ids.map((id) => parseInt(id, 10))
        : [],
    }));
    updateSurvey({ variables: data });
  };

  const handleAddQuestion = () => setQuestionIds((prev) => [...prev, ""]);

  const handleUpdateQuestions = (index, qid) => {
    setQuestionIds((prev) => {
      const updated = [...prev];
      updated[index] = qid;
      return updated;
    });
  };

  const handleDeleteQuestion = (index) =>
    setQuestionIds((prev) => prev.filter((_, i) => i !== index));

  const handleAddUsage = () =>
    setUsage((prev) => [...prev, { client_id: 0, label_ids: [] }]);

  const handleUpdateUsageClientField = (index, cid) => {
    setUsage((prev) => {
      const updated = [...prev];
      updated[index].client_id = cid;
      return updated;
    });
  };

  const handleUpdateUsageLabelsField = (index, values) => {
    setUsage((prev) => {
      const updated = [...prev];
      updated[index].label_ids = values;
      return updated;
    });
  };

  const handleDeleteUsage = (index) =>
    setUsage((prev) => prev.filter((_, i) => i !== index));

  const isPublished = formData && formData.status == "Published" ? true : false;

  if (!formData) return <div></div>;
  if (!questions.length) return <PageSpinner />;
  if (!landlords) return <PageSpinner />;

  return (
    <PageContainer>
      <BlockContainer raised>
        <Row tweaks="border-b border-[#dfe2e8] pb-8 mt-0 mx-5">
          <Col width={12}>
            <Heading
              inset={true}
              text={isPublished ? "View a survey" : "Edit a survey"}
              description={
                isPublished
                  ? "Survey is published. You can now use it to collect responses from clients. Questions are locked. "
                  : "Set up your survey questions, finalize and publish the survey, then assign it to clients to begin collecting responses."
              }
            />
          </Col>
        </Row>
        <FormContainer>
          <FormData>
            <Row tweaks="border-b border-[#dfe2e8] py-7 mx-5">
              <Col width={3}>
                <Row>
                  <Col width={12}>
                    <Heading
                      size="md"
                      text="Survey details"
                      description="Main survey details."
                    />
                  </Col>
                </Row>
              </Col>
              <Col
                width={1}
                extraStyles="border-r border-[#dfe2e8] h-full justify-self-center w-full"
              ></Col>
              <Col width={8}>
                <Row>
                  <Col width={8}>
                    {isPublished && (
                      <ReadOnlyField
                        value={formData.title}
                        label="Survey"
                        isRequired={true}
                      />
                    )}
                    {!isPublished && (
                      <TextField
                        name="title"
                        label="Title"
                        value={formData.title}
                        onChange={handleFieldOnChange}
                        error={fieldError("title")}
                        isRequired={true}
                        minlength={3}
                        maxlength={48}
                        description=""
                      />
                    )}
                  </Col>
                  <Col width={4}>
                    <SelectField
                      value={formData.survey_type_id}
                      values={surveyTypes}
                      label="Survey Type"
                      name="survey_type_id"
                      onChange={(e) => handleFieldOnChange(e, "int")}
                      valueKey="id"
                      labelKey="name"
                      error={fieldError("survey_type_id")}
                      prependOption="Select Type"
                      isDisabled={isPublished}
                      isRequired={true}
                    />
                  </Col>
                  <Col width={12}>
                    {false && (
                      <ReadOnlyField
                        value={formData.description}
                        label="Description"
                        isRequired={true}
                      />
                    )}
                    {true && (
                      <RichTextEditorField
                        name="description"
                        label="Description"
                        value={formData?.description || ""}
                        onChange={handleFieldOnChange}
                        error={fieldError("description")}
                        isRequired={false}
                        maxlength="1024"
                        rows={5}
                      />
                    )}
                  </Col>
                </Row>
              </Col>
            </Row>
            <Row tweaks="border-b border-[#dfe2e8] py-7 mx-5">
              <Col width={3}>
                <Row>
                  <Col width={12}>
                    <Heading
                      size="md"
                      text="Questions"
                      description="Assign questions that should be included into this survey. Only published questions are available."
                    />
                  </Col>
                </Row>
              </Col>
              <Col
                width={1}
                extraStyles="border-r border-[#dfe2e8] h-full justify-self-center w-full"
              ></Col>
              <Col width={8}>
                <Row tweaks="gap-0 sm:grid-cols-1 py-0 px-px">
                  <Col width={12} extraStyles="sm:col-span-1">
                    {questionIds.length > 0 && (
                      <React.Fragment>
                        {isPublished && (
                          <TableContainer tweaks="relative mt-0">
                            <TableView>
                              <THeading>
                                <TRow>
                                  <THCol tweaks="text-left" value="" />
                                </TRow>
                              </THeading>
                              <TContent>
                                {questionIds.map((qid, index) => (
                                  <TRow key={index}>
                                    <TColBorderless tweaks="px-0.5 py-0.5">
                                      <SelectField
                                        label=""
                                        value={qid}
                                        values={questions}
                                        onChange={(e) =>
                                          handleUpdateQuestions(
                                            index,
                                            e.target.value,
                                          )
                                        }
                                        valueKey="id"
                                        labelKey="dropdown_title"
                                        prependOption="Select Question"
                                        isRequired={false}
                                        borderless={true}
                                        isDisabled={true}
                                      />
                                    </TColBorderless>
                                  </TRow>
                                ))}
                              </TContent>
                            </TableView>
                          </TableContainer>
                        )}
                        {!isPublished && (
                          <TableContainer tweaks="relative mt-0">
                            <TableView>
                              <THeading>
                                <TRow>
                                  <THCol tweaks="text-left" value="" />
                                  <THCol
                                    width="w-[38.5px]"
                                    tweaks="px-0.5 py-0.5"
                                  >
                                    <AddButton
                                      label="Add Question"
                                      onClick={handleAddQuestion}
                                      icon
                                    />
                                  </THCol>
                                </TRow>
                              </THeading>
                              <TContent>
                                {questionIds.map((qid, index) => (
                                  <TRow key={index}>
                                    <TColBorderless tweaks="px-0.5 py-0.5">
                                      <SelectField
                                        label=""
                                        value={qid}
                                        values={questions}
                                        onChange={(e) =>
                                          handleUpdateQuestions(
                                            index,
                                            e.target.value,
                                          )
                                        }
                                        valueKey="id"
                                        labelKey="dropdown_title"
                                        prependOption="Select Question"
                                        isRequired={false}
                                        borderless={true}
                                      />
                                    </TColBorderless>

                                    <TColBorderless
                                      width="w-[38.5px]"
                                      tweaks="px-0.5 py-0.5"
                                    >
                                      {questionIds.length > 0 && (
                                        <DeleteButton
                                          icon
                                          onClick={() =>
                                            handleDeleteQuestion(index)
                                          }
                                        />
                                      )}
                                    </TColBorderless>
                                  </TRow>
                                ))}
                              </TContent>
                            </TableView>
                          </TableContainer>
                        )}
                      </React.Fragment>
                    )}
                    {questionIds.length === 0 && (
                      <div className="relative">
                        <div className="-mb-14s">
                          <ItemsNotFound
                            text="No questions yet"
                            description="Click + to add questions."
                          />
                        </div>
                        <div className="absolute top-8 left-0">
                          <AddButton
                            label="Add Question"
                            onClick={handleAddQuestion}
                            icon
                          />
                        </div>
                      </div>
                    )}
                  </Col>
                </Row>
              </Col>
            </Row>

            {isPublished && (
              <Row tweaks="border-b border-[#dfe2e8] py-7 mx-5">
                <Col width={3}>
                  <Row>
                    <Col width={12}>
                      <Heading
                        size="md"
                        text="Usage by clients"
                        description="Assign survey for clients and labels"
                      />
                    </Col>
                  </Row>
                </Col>
                <Col
                  width={1}
                  extraStyles="border-r border-[#dfe2e8] h-full justify-self-center w-full"
                ></Col>
                <Col width={8}>
                  <Row tweaks="gap-0 sm:grid-cols-1 py-0 px-px">
                    <Col width={12} extraStyles="sm:col-span-1">
                      <TableContainer tweaks="relative mt-0">
                        <TableView>
                          <THeading>
                            <TRow>
                              <THCol tweaks="text-left" value="Client" />
                              <THCol tweaks="text-left" value="Labels" />
                              <THCol width="w-[38.5px]" tweaks="px-0.5 py-0.5">
                                <AddButton
                                  label="Add Client Usage"
                                  onClick={handleAddUsage}
                                  icon
                                />
                              </THCol>
                            </TRow>
                          </THeading>
                          <TContent>
                            {usage.map((uObj, index) => (
                              <TRow key={index}>
                                <TColBorderless tweaks="px-0.5 py-0.5">
                                  <SelectField
                                    label=""
                                    value={uObj.client_id}
                                    values={landlords}
                                    onChange={(e) =>
                                      handleUpdateUsageClientField(
                                        index,
                                        e.target.value,
                                      )
                                    }
                                    valueKey="id"
                                    labelKey="account_name"
                                    prependOption="Select Client"
                                    isRequired={false}
                                    borderless={true}
                                  />
                                </TColBorderless>
                                <TColBorderless
                                  tweaks="px-0.5 py-0.5"
                                  truncate={false}
                                >
                                  <MultiSelectField
                                    label=""
                                    value={uObj.label_ids}
                                    values={labels}
                                    onChange={(e) =>
                                      handleUpdateUsageLabelsField(
                                        index,
                                        e.target.value,
                                      )
                                    }
                                    valueKey="id"
                                    labelKey="name"
                                    prependOption="Select Label(s)"
                                    isRequired={false}
                                    borderless={true}
                                  />
                                </TColBorderless>

                                <TColBorderless
                                  width="w-[38.5px]"
                                  tweaks="px-0.5 py-0.5"
                                >
                                  {usage.length > 0 && (
                                    <DeleteButton
                                      icon
                                      onClick={() => handleDeleteUsage(index)}
                                    />
                                  )}
                                </TColBorderless>
                              </TRow>
                            ))}
                          </TContent>
                        </TableView>
                      </TableContainer>
                    </Col>
                  </Row>
                </Col>
              </Row>
            )}
          </FormData>
          <FormControls>
            <CancelButton onClick={goBack} />
            <SaveButton onClick={handleSave} loading={isUpdating} />
          </FormControls>
        </FormContainer>
      </BlockContainer>
    </PageContainer>
  );
};

export default SurveyCreate;
